/**
 * Módulo 2 del tutorial: Ubicaciones — cuadros vs corrales (KAN-232).
 *
 * Explica la diferencia entre CORRAL (engorde a balanceado, alimenta el
 * cálculo de costos) y CUADRO (pastura) y guía la creación REAL de la
 * primera ubicación eligiendo el tipo.
 *
 * Anclas: ubicacion-new-button, ubicacion-form-nombre,
 * ubicacion-form-campo, ubicacion-form-tipo, btn-save-ubicacion.
 */

import type { TutorialModuleDef } from '../../../services/tutorial/types';
import { getRepositories } from '../../../services/storage';

export const ubicacionesModule: TutorialModuleDef = {
  id: 'ubicaciones',
  title: 'Ubicaciones: cuadros y corrales',
  description: 'Entendé la diferencia entre cuadro y corral, y cargá tu primera ubicación del campo.',
  emoji: '🧱',
  image: 'images/tutorial/ubicaciones.png',
  permission: { module: 'locations', action: 'create' },
  grupo: 'esencial',
  route: '/ubicaciones',
  steps: [
    {
      id: 'concepto',
      route: '/tutorial',
      text: 'Dentro de tu campo tenés ubicaciones. Hay dos tipos: CORRAL (feed-lot, engorde a balanceado) y CUADRO (pastura). El tipo cambia cómo calcula la app tus costos.',
      narration:
        'Ahora vamos con las ubicaciones, que son los pedazos de tu campo. Fijate bien que hay dos tipos, y no es lo mismo uno que otro. El corral es para engorde a balanceado: ahí la app te calcula el costo del kilo producido. El cuadro es pastura: pasto directo del campo. Contale a la app dónde come cada animal y ella hace las cuentas por vos.',
    },
    {
      id: 'nueva-ubicacion',
      route: '/ubicaciones',
      target: '[data-ar-guide="ubicacion-new-button"]',
      label: 'Nueva ubicación',
      text: 'Creemos tu primera ubicación: tocá "Nueva ubicación".',
      narration:
        'Vamos a cargar tu primera ubicación, así después sabés dónde está cada animal. Tocá el botón Nueva ubicación y llenamos el formulario juntos.',
    },
    {
      id: 'nombre',
      route: '/ubicaciones/new',
      target: '[data-ar-guide="ubicacion-form-nombre"]',
      advanceOnClick: false,
      label: 'Nombre',
      text: 'Ponele nombre: como le decís en el campo (ej. "Corral de atrás", "Cuadro del molino").',
      narration:
        'Ponele nombre a la ubicación, como le decís en el campo de verdad. Corral de atrás, Cuadro del molino, lo que sea: que lo reconozcas al verlo.',
    },
    {
      id: 'campo',
      route: '/ubicaciones/new',
      target: '[data-ar-guide="ubicacion-form-campo"]',
      advanceOnClick: false,
      label: 'Campo',
      text: 'Elegí a qué campo pertenece (si hiciste el módulo anterior, ya lo tenés cargado).',
      narration:
        'Ahora decile a qué campo pertenece esta ubicación. Si venís del paso anterior, tu campo ya está en la lista: elegilo acá.',
    },
    {
      id: 'tipo',
      route: '/ubicaciones/new',
      target: '[data-ar-guide="ubicacion-form-tipo"]',
      advanceOnClick: false,
      label: 'Tipo: corral o cuadro',
      text: 'Acá está la clave: CORRAL = engorde a balanceado (calcula costos de engorde). CUADRO = pastura. Elegí según cómo se maneja ese sector.',
      narration:
        'Y acá viene lo importante, así que prestá atención. Si el sector es de engorde a balanceado, con comedero y todo el aparato, elegí corral: la app te va a calcular el costo del engorde, kilo por kilo. Y si es pastura, pasto a pié de campo, elegí cuadro. No te equivoques, porque de acá dependen todas las cuentas después.',
    },
    {
      id: 'guardar',
      route: '/ubicaciones/new',
      target: '[data-ar-guide="btn-save-ubicacion"]',
      label: 'Crear Ubicación',
      text: 'Tocá "Crear Ubicación" para guardarla.',
      narration:
        'Bueno, ya está todo cargado. Apretá Crear Ubicación y quedó guardada. Después podés cargar todas las que necesites, una por una.',
    },
  ],
  completionCheck: async () => {
    const ubicaciones = await getRepositories().ubicacion.getAllForActiveEmpresa();
    return ubicaciones.length > 0;
  },
};
